import { useCallback, useEffect, useState } from "react";
import {
  adminListAssociations,
  adminListTodos,
  adminSetTodoDone,
  listMyTodos,
  setTodoDone,
  type AdminTodo,
  type Todo,
} from "@/lib/api";
import { useAuth } from "@/lib/hooks/useAuth";
import { formatDateTime } from "@/lib/format";
import { AdminPagination } from "./AdminPagination";

type StatusFilter = "open" | "done" | "all";
type SortOrder = "newest" | "oldest";
const PAGE = 50;

const FILTER_OPTIONS: { key: StatusFilter; label: string }[] = [
  { key: "open", label: "Open" },
  { key: "done", label: "Afgevinkt" },
  { key: "all", label: "Alles" },
];

function StatusPills({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 rounded-full border border-[rgba(10,52,96,0.10)] bg-white p-1 shadow-[0_4px_12px_rgba(10,52,96,0.04)] w-fit">
      {FILTER_OPTIONS.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            value === opt.key
              ? "bg-[var(--cs-rose)] text-white shadow-[0_8px_18px_rgba(225,29,72,0.20)]"
              : "text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)]"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function DoneCheckbox({
  done,
  disabled,
  onToggle,
}: {
  done: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-label={done ? "Markeer als niet afgevinkt" : "Markeer als afgevinkt"}
      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
        done
          ? "border-emerald-600 bg-emerald-600 text-white"
          : "border-[rgba(10,52,96,0.30)] bg-white"
      } disabled:opacity-50`}
    >
      {done ? (
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : null}
    </button>
  );
}

function doneLabel(todo: Todo, doneAt: string): string {
  const by = todo.done_by_name ? ` door ${todo.done_by_name}` : "";
  return `Afgevinkt${by} op ${formatDateTime(doneAt)}`;
}

function MyTodos() {
  const [items, setItems] = useState<Todo[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await listMyTodos();
      setItems(resp.todos ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = items.filter((t) =>
    statusFilter === "all" ? true : statusFilter === "done" ? !!t.done_at : !t.done_at,
  );

  const toggle = async (todo: Todo) => {
    setBusyId(todo.id);
    try {
      const updated = await setTodoDone(todo.id, !todo.done_at);
      setItems((prev) => prev.map((t) => (t.id === todo.id ? updated : t)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <StatusPills value={statusFilter} onChange={setStatusFilter} />
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      ) : null}
      {loading ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">
          Geen todo's. Zeg "CarmenTodo" gevolgd door een taak aan het einde van je dictaat.
        </p>
      ) : (
        <div className="space-y-2">
          {visible.map((todo) => (
            <div
              key={todo.id}
              className="flex items-start gap-3 rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card px-4 py-3 text-sm"
            >
              <DoneCheckbox
                done={!!todo.done_at}
                disabled={busyId === todo.id}
                onToggle={() => void toggle(todo)}
              />
              <div className="min-w-0">
                <p className={`text-[var(--cs-navy)] ${todo.done_at ? "line-through opacity-60" : ""}`}>
                  {todo.text}
                </p>
                <p className="mt-0.5 text-[11px] text-[var(--cs-ink-muted)]">
                  {todo.recording_label} · {formatDateTime(todo.created_at)}
                </p>
                {todo.done_at ? (
                  <p className="mt-0.5 text-[11px] text-emerald-700">
                    {doneLabel(todo, todo.done_at)}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OversightTodos({
  showAssociationFilter,
  canCheck,
}: {
  showAssociationFilter: boolean;
  /** Secretariat may check off / reopen; superuser oversight is read-only. */
  canCheck: boolean;
}) {
  const [items, setItems] = useState<AdminTodo[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [associationId, setAssociationId] = useState("");
  const [sort, setSort] = useState<SortOrder>("newest");
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await adminListTodos({
        associationId: associationId || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        offset,
        limit: PAGE,
        sort,
      });
      setItems(resp.todos ?? []);
      setTotal(resp.total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [associationId, statusFilter, offset, sort]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setOffset(0);
  }, [associationId, statusFilter, sort]);

  // The row stays put after a toggle (even under the "Open" filter) so a
  // mis-click can be undone right away; the next load re-filters.
  const toggle = async (todo: AdminTodo) => {
    setBusyId(todo.id);
    setError(null);
    try {
      const updated = await adminSetTodoDone(todo.id, !todo.done_at);
      setItems((prev) => prev.map((t) => (t.id === todo.id ? updated : t)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  // The superuser association filter lists every association (not just the
  // ones on the current page) — loaded once from the admin associations list.
  const [assocOptions, setAssocOptions] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (!showAssociationFilter) return;
    adminListAssociations().then(
      (list) => setAssocOptions([...list].sort((a, b) => a.name.localeCompare(b.name))),
      () => {}, // non-fatal: dropdown just stays empty
    );
  }, [showAssociationFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <StatusPills value={statusFilter} onChange={setStatusFilter} />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value === "oldest" ? "oldest" : "newest")}
          className="rounded-lg border border-[rgba(10,52,96,0.18)] bg-white px-3 py-1.5 text-xs"
        >
          <option value="newest">Nieuwste eerst</option>
          <option value="oldest">Oudste eerst</option>
        </select>
        {showAssociationFilter ? (
          <select
            value={associationId}
            onChange={(e) => setAssociationId(e.target.value)}
            className="rounded-lg border border-[rgba(10,52,96,0.18)] bg-white px-3 py-1.5 text-xs"
          >
            <option value="">Alle associaties</option>
            {assocOptions.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        ) : null}
      </div>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      ) : null}
      {loading ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Geen todo's gevonden.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
          <table className="w-full text-sm">
            <thead className="bg-[var(--cs-ivory)]">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                {canCheck ? <th className="w-10 px-4 py-2"><span className="sr-only">Afvinken</span></th> : null}
                <th className="px-4 py-2">Datum</th>
                <th className="px-4 py-2">Gebruiker</th>
                {showAssociationFilter ? <th className="px-4 py-2">Associatie</th> : null}
                <th className="px-4 py-2">Opname</th>
                <th className="px-4 py-2">Todo</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
              {items.map((item) => (
                <tr key={item.id} className="text-xs">
                  {canCheck ? (
                    <td className="px-4 py-2">
                      <DoneCheckbox
                        done={!!item.done_at}
                        disabled={busyId === item.id}
                        onToggle={() => void toggle(item)}
                      />
                    </td>
                  ) : null}
                  <td className="px-4 py-2 text-[rgba(10,52,96,0.78)]">{formatDateTime(item.created_at)}</td>
                  <td className="px-4 py-2">
                    <p className="font-medium text-[var(--cs-navy)]">
                      {item.user_full_name || item.user_email || "—"}
                    </p>
                    {item.user_full_name && item.user_email ? (
                      <p className="text-[10px] text-[var(--cs-ink-muted)]">{item.user_email}</p>
                    ) : null}
                  </td>
                  {showAssociationFilter ? (
                    <td className="px-4 py-2 text-[var(--cs-navy)]">{item.association_name || "—"}</td>
                  ) : null}
                  <td className="px-4 py-2 font-mono text-[11px] text-[var(--cs-navy)]">{item.recording_label}</td>
                  <td className="max-w-[320px] px-4 py-2 text-[var(--cs-navy)]">{item.text}</td>
                  <td className="px-4 py-2">
                    {item.done_at ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                        {doneLabel(item, item.done_at)}
                      </span>
                    ) : (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                        Open
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminPagination
        offset={offset}
        limit={PAGE}
        total={total}
        onPrev={() => setOffset((o) => Math.max(0, o - PAGE))}
        onNext={() => setOffset((o) => o + PAGE)}
      />
    </div>
  );
}

export function AdminTodosTab() {
  const { accessRole } = useAuth();
  if (accessRole === "user") return <MyTodos />;
  return (
    <OversightTodos
      showAssociationFilter={accessRole === "superuser"}
      canCheck={accessRole === "afdeling_secretary"}
    />
  );
}
